import React, {useEffect, useState, useRef, useMemo, useCallback} from "react";
import {Button, Popconfirm, Space, Switch} from "antd";
import {ArrowRightOutlined} from "@ant-design/icons";

import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {H3Map} from "../../../H3Map/H3Map";
import PoolSupporterEditModal from "./PoolSupporterEditModal";
import Legend from "./Legend";
import DefinePoolModal from "./DefinePoolModal";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {
    PageContainer,
    HeaderSection,
    PageTitle,
    MapContainer,
    SectionHeader,
    SectionColorIndicator,
    SectionTitle
} from "./PoolSupporter.styles";

export const COLORS = {
    OWN_POOL: "#1976D2",
    SUPPORTING_POOL: "#e5790a",
    SUPPORTED_POOL: "#43A047",
    BOTH_SUPPORTING_AND_SUPPORTED: "#9C27B0"
};

const PoolSupporter = ({poolId}) => {
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editInitialValues, setEditInitialValues] = useState({});
    const [hiddenSupporters, setHiddenSupporters] = useState([]);
    const [hiddenSupportings, setHiddenSupportings] = useState([]);
    const [showSelf, setShowSelf] = useState(true);
    const [showUnknown, setShowUnknown] = useState(true);
    const [definePoolModalVisible, setDefinePoolModalVisible] = useState(false);
    const [definingPoolInfo, setDefiningPoolInfo] = useState(null);
    const stores = useStore();
    const mapRef = useRef(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const handleDefinePool = useCallback(
        (poolIdToDefine, poolNameToDefine) => {
            setDefiningPoolInfo({id: poolIdToDefine, name: poolNameToDefine});
            setDefinePoolModalVisible(true);
        },
        [setDefiningPoolInfo, setDefinePoolModalVisible]
    );

    const handleEdit = useCallback(
        record => {
            setEditInitialValues({
                name: record?.supporterPool?.name || "",
                returningCourierDistance: record?.returningCourierAssign?.distance || 0,
                returningCourierAssignActive: record?.returningCourierAssign?.isActive || false,
                supportWaitTime: record?.supporterPoolCourierAssign?.waitTime || 0,
                supporterCourierAssignActive: record?.supporterPoolCourierAssign?.isActive || false,
                supportDistance: record?.supporterPoolCourierAssign?.distance || 0
            });
            setEditModalVisible(true);
        },
        [setEditInitialValues, setEditModalVisible]
    );

    const [
        isPoolSupportersLoading,
        poolSupporters,
        setPoolSupporters,
        setPoolSupportersLoading,
        refetchPoolSupporters
    ] = useFetch({
        fetchFn: stores.poolStore.getPoolSupporters.bind(null, {poolId}),
        initialCall: true
    });

    const [
        isPoolSupportingsLoading,
        poolSupportings,
        setPoolSupportings,
        setPoolSupportingsLoading,
        refetchPoolSupportings
    ] = useFetch({
        fetchFn: stores.poolStore.getPoolSupportings.bind(null, {poolId}),
        initialCall: true
    });

    const [loading, pool] = useFetch({
        fetchFn: stores.poolStore.getPool.bind(null, {poolId}),
        initialCall: true
    });

    const [isCityH3CellsLoading, cityH3Cells] = useFetch({
        fetchFn: stores.poolStore.getPoolH3Cells.bind(null, {city: pool?.city}),
        dependencies: [pool?.city],
        initialCall: !!pool?.city
    });

    useEffect(() => {
        if (!poolSupporters || !Array.isArray(poolSupporters)) {
            return;
        }

        const definePoolHandlerLocal = event => {
            const {poolId: poolIdToDefine, poolName: poolNameToDefine} = event.detail;
            handleDefinePool(poolIdToDefine, poolNameToDefine);
        };

        const filterPoolHandlerLocal = event => {
            const clickedPoolId = event.detail.poolId;
            setSelectedCategory(prevSelectedCategory =>
                prevSelectedCategory === clickedPoolId ? null : clickedPoolId
            );
        };

        const editPoolSupporterHandlerLocal = event => {
            const {supporterPoolIdToEdit} = event.detail;
            const supporterToEdit = poolSupporters.find(
                s => s.supporterPool?._id === supporterPoolIdToEdit
            );
            if (supporterToEdit) {
                handleEdit(supporterToEdit);
            }
        };

        document.removeEventListener("definePoolClicked", definePoolHandlerLocal);
        document.removeEventListener("filterPoolClicked", filterPoolHandlerLocal);
        document.removeEventListener("editPoolSupporterClicked", editPoolSupporterHandlerLocal);

        document.addEventListener("definePoolClicked", definePoolHandlerLocal);
        document.addEventListener("filterPoolClicked", filterPoolHandlerLocal);
        document.addEventListener("editPoolSupporterClicked", editPoolSupporterHandlerLocal);

        return () => {
            document.removeEventListener("definePoolClicked", definePoolHandlerLocal);
            document.removeEventListener("filterPoolClicked", filterPoolHandlerLocal);
            document.removeEventListener("editPoolSupporterClicked", editPoolSupporterHandlerLocal);
        };
    }, [poolSupporters, handleDefinePool, handleEdit, setSelectedCategory]);

    useEffect(() => {
        if (pool?.city && mapRef.current) {
            setTimeout(() => {
                if (mapRef.current?.panToCity) {
                    mapRef.current.panToCity(pool.city);
                }
            }, 1000);
        }
    }, [pool?.city]);

    function buildButtonHtml(text, onClickAction, backgroundColor) {
        const escapedOnClickActionForHtmlAttribute = onClickAction.replace(/"/g, "&quot;");
        return `
            <button 
                onclick="${escapedOnClickActionForHtmlAttribute}" 
                style="margin-top: 5px; margin-right: 5px; padding: 4px 8px; color: white; border: none; border-radius: 3px; cursor: pointer; background: ${backgroundColor}; font-size: 12px; line-height: 1.2; outline: none; box-shadow: none;">
                ${text}
            </button>
        `.trim();
    }

    function buildTooltipContentHtml(lines, buttonsHtmlArray) {
        const linesHtml = lines.map(line => `<div>${line}</div>`).join("\n");
        const buttonsContainerHtml =
            buttonsHtmlArray && buttonsHtmlArray.length > 0
                ? `<div style="margin-top: 8px;">${buttonsHtmlArray.join("")}</div>`
                : "";
        return `
            <div class="tooltip-content">
                ${linesHtml}
                ${buttonsContainerHtml}
            </div>
        `.trim();
    }

    function getCommonPoolColumnConfigs(onSwitchHandler) {
        return [
            {
                title: "Geri Dönen Kurye Atama Mesafesi (m)",
                key: "returningCourierDistance",
                onCell: () => ({"data-label": "Geri Dönen Kurye Atama Mesafesi (m)"}),
                render: (_, record) => record?.returningCourierAssign?.distance || 0
            },
            {
                title: "Destek İçin Beklenecek Sipariş Süresi (dk)",
                key: "supportWaitTime",
                onCell: () => ({"data-label": "Destek İçin Beklenecek Sipariş Süresi (dk)"}),
                render: (_, record) => record?.supporterPoolCourierAssign?.waitTime || 0
            },
            {
                title: "Destekçi Atama Mesafesi (m)",
                key: "supportDistance",
                onCell: () => ({"data-label": "Destekçi Atama Mesafesi (m)"}),
                render: (_, record) => record?.supporterPoolCourierAssign?.distance || 0
            },
            {
                title: "Durum",
                key: "status",
                align: "center",
                onCell: () => ({"data-label": "Durum"}),
                render: (_, record) => (
                    <div style={{display: "flex", justifyContent: "center"}}>
                        <Switch
                            checked={record?.isActive || false}
                            checkedChildren="Aktif"
                            unCheckedChildren="Pasif"
                            onChange={async checked => {
                                onSwitchHandler(record, checked);
                            }}
                        />
                    </div>
                )
            }
        ];
    }

    function getH3CellsForPools() {
        if (!cityH3Cells?.data || !poolSupporters || !poolSupportings)
            return {
                ownHexagons: [],
                supportingHexagons: [],
                supportedHexagons: [],
                neighborHexagons: []
            };

        const usedH3Indices = new Set();

        const currentPoolDetail = cityH3Cells.data.find(p => p._id === poolId);
        const ownHexagonsList =
            currentPoolDetail?.h3Cells.map(cell => {
                usedH3Indices.add(cell.index);
                const poolNameForJs = (currentPoolDetail?.name || "-").replace(/'/g, "\\'");
                const lines = [`Havuz: ${poolNameForJs}`, `Hexagon ID: ${cell.index}`];

                const currentPoolId = currentPoolDetail?._id;
                const filterButtonText =
                    selectedCategory === currentPoolId ? "Filtreyi Kaldır" : "Filtrele";
                const filterBtnJs = `document.dispatchEvent(new CustomEvent('filterPoolClicked', { detail: { poolId: '${currentPoolId}' } })); setTimeout(() => { const closeBtn = document.getElementById('custom-tooltip-close'); if (closeBtn) { closeBtn.click(); } }, 0);`;
                const detailBtnJs = `window.open('/panel/pools/${currentPoolId}', '_blank')`;

                const filterButtonHtml = buildButtonHtml(filterButtonText, filterBtnJs, "#607D8B");
                const detailButtonHtml = buildButtonHtml("Detay", detailBtnJs, "#757575");

                const buttons = [filterButtonHtml, detailButtonHtml];

                const isAlreadySupporter = poolSupporters.some(
                    supporter => supporter.supporterPool?._id === currentPoolId
                );
                if (!isAlreadySupporter) {
                    const escapedPoolNameForEvent = (currentPoolDetail?.name || "")
                        .replace(/'/g, "\\'")
                        .replace(/\\/g, "\\\\");
                    const onClickJsDefine = `document.dispatchEvent(new CustomEvent('definePoolClicked', { detail: { poolId: '${currentPoolId}', poolName: '${escapedPoolNameForEvent}' } })); setTimeout(() => { const closeBtn = document.getElementById('custom-tooltip-close'); if (closeBtn) { closeBtn.click(); } }, 0);`;
                    const defineButtonHtml = buildButtonHtml(
                        "➕ Tanımla",
                        onClickJsDefine,
                        "#9E9E9E"
                    );
                    buttons.push(defineButtonHtml);
                }

                const content = buildTooltipContentHtml(lines, buttons);

                return {
                    index: cell.index,
                    color: COLORS.OWN_POOL,
                    poolId: currentPoolId,
                    showTooltip: true,
                    tooltip: {
                        title: "Havuz Bölgesi",
                        content
                    }
                };
            }) || [];

        const supportingPoolsHexagonsList = poolSupportings
            .filter(supporting => supporting.isActive)
            .map(supporting => {
                const poolData = cityH3Cells.data.find(p => p._id === supporting.pool?._id);
                return (
                    poolData?.h3Cells.map(cell => {
                        usedH3Indices.add(cell.index);
                        const poolNameForJs = (poolData?.name || "-").replace(/'/g, "\\'");
                        const statusText = supporting.isActive ? "Aktif" : "Pasif";
                        const lines = [
                            `Havuz: ${poolNameForJs}`,
                            `Hexagon ID: ${cell.index}`,
                            `Durum: ${statusText}`
                        ];

                        const targetPoolId = poolData?._id;
                        const filterButtonText =
                            selectedCategory === targetPoolId ? "Filtreyi Kaldır" : "Filtrele";
                        const filterBtnJs = `document.dispatchEvent(new CustomEvent('filterPoolClicked', { detail: { poolId: '${targetPoolId}' } })); setTimeout(() => { const closeBtn = document.getElementById('custom-tooltip-close'); if (closeBtn) { closeBtn.click(); } }, 0);`;
                        const detailBtnJs = `window.open('/panel/pools/${targetPoolId}', '_blank')`;

                        const filterButtonHtml = buildButtonHtml(
                            filterButtonText,
                            filterBtnJs,
                            "#607D8B"
                        );
                        const detailButtonHtml = buildButtonHtml("Detay", detailBtnJs, "#757575");

                        const buttons = [filterButtonHtml, detailButtonHtml];

                        const isAlreadySupporter = poolSupporters.some(
                            supporter => supporter.supporterPool?._id === targetPoolId
                        );
                        if (!isAlreadySupporter) {
                            const escapedPoolNameForEvent = (poolData?.name || "")
                                .replace(/'/g, "\\'")
                                .replace(/\\/g, "\\\\");
                            const onClickJsDefine = `document.dispatchEvent(new CustomEvent('definePoolClicked', { detail: { poolId: '${targetPoolId}', poolName: '${escapedPoolNameForEvent}' } })); setTimeout(() => { const closeBtn = document.getElementById('custom-tooltip-close'); if (closeBtn) { closeBtn.click(); } }, 0);`;
                            const defineButtonHtml = buildButtonHtml(
                                "➕ Tanımla",
                                onClickJsDefine,
                                "#9E9E9E"
                            );
                            buttons.push(defineButtonHtml);
                        }

                        const content = buildTooltipContentHtml(lines, buttons);

                        return {
                            index: cell.index,
                            color: COLORS.SUPPORTING_POOL,
                            poolId: targetPoolId,
                            showTooltip: true,
                            tooltip: {
                                title: "Destek Verilen Havuz",
                                content
                            }
                        };
                    }) || []
                );
            })
            .flat();

        const supportedPoolsHexagonsList = poolSupporters
            .filter(supporter => supporter.isActive)
            .map(supporter => {
                const poolData = cityH3Cells.data.find(p => p._id === supporter.supporterPool?._id);
                return (
                    poolData?.h3Cells.map(cell => {
                        usedH3Indices.add(cell.index);
                        const poolNameForJs = (poolData?.name || "-").replace(/'/g, "\\'");
                        const statusText = supporter.isActive ? "Aktif" : "Pasif";
                        const lines = [
                            `Havuz: ${poolNameForJs}`,
                            `Hexagon ID: ${cell.index}`,
                            `Durum: ${statusText}`
                        ];

                        const isAlsoSupporting = poolSupportings.some(
                            supporting => supporting.pool?._id === poolData?._id
                        );

                        if (isAlsoSupporting) {
                            lines.push("⚠️ Bu havuz aynı zamanda destek veren havuzdur");
                        }

                        const targetPoolId = poolData?._id;
                        const filterButtonText =
                            selectedCategory === targetPoolId ? "Filtreyi Kaldır" : "Filtrele";
                        const filterBtnJs = `document.dispatchEvent(new CustomEvent('filterPoolClicked', { detail: { poolId: '${targetPoolId}' } })); setTimeout(() => { const closeBtn = document.getElementById('custom-tooltip-close'); if (closeBtn) { closeBtn.click(); } }, 0);`;
                        const detailBtnJs = `window.open('/panel/pools/${targetPoolId}', '_blank')`;
                        const editBtnJs = `document.dispatchEvent(new CustomEvent('editPoolSupporterClicked', { detail: { supporterPoolIdToEdit: '${targetPoolId}' } }))`;

                        const filterButtonHtml = buildButtonHtml(
                            filterButtonText,
                            filterBtnJs,
                            "#607D8B"
                        );
                        const detailButtonHtml = buildButtonHtml("Detay", detailBtnJs, "#757575");
                        const editButtonHtml = buildButtonHtml(
                            "Düzenle",
                            editBtnJs,
                            COLORS.SUPPORTED_POOL
                        );

                        const buttons = [filterButtonHtml, detailButtonHtml, editButtonHtml];

                        const isAlreadySupporter = poolSupporters.some(
                            supporter => supporter.supporterPool?._id === targetPoolId
                        );
                        if (!isAlreadySupporter) {
                            const escapedPoolNameForEvent = (poolData?.name || "")
                                .replace(/'/g, "\\'")
                                .replace(/\\/g, "\\\\");
                            const onClickJsDefine = `document.dispatchEvent(new CustomEvent('definePoolClicked', { detail: { poolId: '${targetPoolId}', poolName: '${escapedPoolNameForEvent}' } })); setTimeout(() => { const closeBtn = document.getElementById('custom-tooltip-close'); if (closeBtn) { closeBtn.click(); } }, 0);`;
                            const defineButtonHtml = buildButtonHtml(
                                "➕ Tanımla",
                                onClickJsDefine,
                                "#9E9E9E"
                            );
                            buttons.push(defineButtonHtml);
                        }

                        const content = buildTooltipContentHtml(lines, buttons);

                        return {
                            index: cell.index,
                            color: isAlsoSupporting
                                ? COLORS.BOTH_SUPPORTING_AND_SUPPORTED
                                : COLORS.SUPPORTED_POOL,
                            poolId: targetPoolId,
                            showTooltip: true,
                            tooltip: {
                                title: "Destek Alınan Havuz",
                                content
                            }
                        };
                    }) || []
                );
            })
            .flat();

        const inactiveSupporterIds = poolSupporters
            .filter(s => s.isActive === false && s.supporterPool?._id)
            .map(s => s.supporterPool._id);
        const inactiveSupportingIds = poolSupportings
            .filter(s => s.isActive === false && s.pool?._id)
            .map(s => s.pool._id);
        const inactivePoolIds = new Set([...inactiveSupporterIds, ...inactiveSupportingIds]);

        const neighborHexagonsList = cityH3Cells.data
            .filter(
                poolInCity => poolInCity.isActive !== false && !inactivePoolIds.has(poolInCity._id)
            )
            .flatMap(poolInCity =>
                poolInCity.h3Cells
                    .filter(cell => !usedH3Indices.has(cell.index))
                    .map(cell => {
                        const poolName = poolInCity?.name || "-";
                        const targetPoolId = poolInCity?._id;
                        const escapedPoolNameForEvent = (poolInCity?.name || "")
                            .replace(/'/g, "\\'")
                            .replace(/\\/g, "\\\\");
                        const lines = [
                            `Havuz: ${poolName} (${targetPoolId || "ID Yok"})`,
                            `Hexagon ID: ${cell.index}`
                        ];
                        const onClickJsDefine = `document.dispatchEvent(new CustomEvent('definePoolClicked', { detail: { poolId: '${targetPoolId}', poolName: '${escapedPoolNameForEvent}' } })); setTimeout(() => { const closeBtn = document.getElementById('custom-tooltip-close'); if (closeBtn) { closeBtn.click(); } }, 0);`;
                        const defineButtonHtml = buildButtonHtml(
                            "➕ Tanımla",
                            onClickJsDefine,
                            "#9E9E9E"
                        );

                        const detailBtnJs = `window.open('/panel/pools/${targetPoolId}', '_blank')`;
                        const detailButtonHtml = buildButtonHtml("Detay", detailBtnJs, "#757575");

                        const content = buildTooltipContentHtml(lines, [
                            defineButtonHtml,
                            detailButtonHtml
                        ]);

                        return {
                            index: cell.index,
                            showTooltip: true,
                            tooltip: {
                                title: "Tanımsız Bölge",
                                content
                            },
                            poolId: targetPoolId
                        };
                    })
            )
            .filter(Boolean);

        return {
            ownHexagons: ownHexagonsList,
            supportingHexagons: supportingPoolsHexagonsList,
            supportedHexagons: supportedPoolsHexagonsList,
            neighborHexagons: neighborHexagonsList
        };
    }

    const {
        ownHexagons,
        supportingHexagons,
        supportedHexagons,
        neighborHexagons
    } = getH3CellsForPools();

    const {filteredOwnHexagons, filteredOtherHexagons} = useMemo(() => {
        let ownResult, otherResult;
        if (
            selectedCategory &&
            !["own", "supporting", "supported", "unknown", "both", null, undefined].includes(
                selectedCategory
            )
        ) {
            const targetPoolId = selectedCategory;

            const ownPoolIsTarget = poolId === targetPoolId;
            ownResult = ownPoolIsTarget && showSelf ? ownHexagons : [];

            const potentialOthers = [];
            potentialOthers.push(
                ...supportedHexagons.filter(hex => {
                    const supporter = poolSupporters.find(s => s.supporterPool?._id === hex.poolId);
                    return supporter && !hiddenSupporters.includes(supporter._id);
                })
            );
            potentialOthers.push(
                ...supportingHexagons.filter(hex => {
                    const supporting = poolSupportings.find(s => s.pool?._id === hex.poolId);
                    return supporting && !hiddenSupportings.includes(supporting._id);
                })
            );
            if (showUnknown) {
                potentialOthers.push(...neighborHexagons);
            }
            otherResult = potentialOthers.filter(hex => hex.poolId === targetPoolId);

            if (ownPoolIsTarget) {
                otherResult = otherResult.filter(hex => hex.poolId !== poolId);
            }
        } else if (selectedCategory === "both") {
            ownResult = [];
            const bothPoolIds = poolSupporters
                .filter(
                    sup =>
                        poolSupportings.some(sing => sing.pool?._id === sup.supporterPool?._id) &&
                        !hiddenSupporters.includes(sup._id)
                )
                .map(sup => sup.supporterPool?._id)
                .filter(id => {
                    const supporting = poolSupportings.find(sing => sing.pool?._id === id);
                    return !supporting || !hiddenSupportings.includes(supporting._id);
                });

            otherResult = [...supportedHexagons, ...supportingHexagons].filter(hex =>
                bothPoolIds.includes(hex.poolId)
            );
        } else if (selectedCategory === "unknown") {
            ownResult = [];
            otherResult = neighborHexagons;
        } else if (selectedCategory === "own") {
            ownResult = showSelf ? ownHexagons : [];
            otherResult = [];
        } else if (selectedCategory === "supporting") {
            ownResult = [];
            otherResult = supportingHexagons.filter(hex => {
                const supportingId = poolSupportings.find(s => s.pool?._id === hex.poolId)?._id;
                return supportingId !== undefined && !hiddenSupportings.includes(supportingId);
            });
        } else if (selectedCategory === "supported") {
            ownResult = [];
            otherResult = supportedHexagons.filter(hex => {
                const supporterId = poolSupporters.find(s => s.supporterPool?._id === hex.poolId)
                    ?._id;
                return supporterId !== undefined && !hiddenSupporters.includes(supporterId);
            });
        } else {
            ownResult = showSelf ? ownHexagons : [];
            otherResult = [
                ...supportedHexagons.filter(hex => {
                    const supporterId = poolSupporters.find(
                        s => s.supporterPool?._id === hex.poolId
                    )?._id;
                    return supporterId !== undefined && !hiddenSupporters.includes(supporterId);
                }),
                ...supportingHexagons.filter(hex => {
                    const supportingId = poolSupportings.find(s => s.pool?._id === hex.poolId)?._id;
                    return supportingId !== undefined && !hiddenSupportings.includes(supportingId);
                }),
                ...(showUnknown ? neighborHexagons : [])
            ];
        }
        return {filteredOwnHexagons: ownResult, filteredOtherHexagons: otherResult};
    }, [
        selectedCategory,
        ownHexagons,
        supportingHexagons,
        supportedHexagons,
        neighborHexagons,
        showSelf,
        poolSupporters,
        hiddenSupporters,
        poolSupportings,
        hiddenSupportings,
        showUnknown,
        poolId
    ]);

    async function handleDelete(record) {
        try {
            await stores.poolStore.deletePoolSupporter({
                poolId,
                supportId: record._id
            });
            if (typeof stores.poolStore.getPoolSupporters === "function") {
                refetchPoolSupporters();
            }
            if (typeof stores.poolStore.getPoolSupportings === "function") {
                refetchPoolSupportings();
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function handleSwitch(record, checked) {
        await stores.poolStore.updateSupporterActive({
            poolId,
            supportId: record._id,
            isActive: checked
        });
        refetchPoolSupporters();
    }
    function handleEditModalCancel() {
        setEditModalVisible(false);
    }
    async function handleEditModalSave(values) {
        try {
            const editingSupporter = poolSupporters.find(
                s => s.supporterPool?.name === values.name
            );

            if (!editingSupporter) {
                throw new Error("Supporter not found");
            }

            await stores.poolStore.updatePoolSupporter({
                poolId,
                supportId: editingSupporter._id,
                returningCourierAssign: {
                    isActive: values.returningCourierAssignActive,
                    distance: values.returningCourierDistance
                },
                supporterPoolCourierAssign: {
                    isActive: values.supporterCourierAssignActive,
                    distance: values.supportDistance,
                    waitTime: values.supportWaitTime
                }
            });
            setEditModalVisible(false);
            refetchPoolSupporters();
        } catch (error) {
            console.error(error);
        }
    }

    async function handleDefinePoolSubmit(values) {
        if (!definingPoolInfo) {
            return;
        }

        try {
            const payload = {
                supporterPool: definingPoolInfo.id,
                returningCourierAssign: {
                    isActive: values.returningCourierAssignIsActive,
                    distance: values.returningCourierAssignDistance
                },
                supporterPoolCourierAssign: {
                    isActive: values.supporterPoolCourierAssignIsActive,
                    distance: values.supporterPoolCourierAssignDistance,
                    waitTime: values.supporterPoolCourierAssignWaitTime
                }
            };

            await stores.poolStore.createPoolSupporter({poolId, ...payload});

            setDefinePoolModalVisible(false);
            refetchPoolSupporters();
            refetchPoolSupportings();
        } catch (error) {
            console.error("PoolSupporter: handleDefinePoolSubmit error", error);
        }
    }

    function getColumns(onEdit, onDelete, onSwitch, type = "supporters") {
        const poolNameColumn = {
            title: "Havuz Adı",
            key: "poolName",
            width: 200,
            onCell: () => ({"data-label": "Havuz Adı"}),
            render: (_, record) => {
                const poolInfo = type === "supportings" ? record?.pool : record?.supporterPool;
                const link = `/panel/pools/${poolInfo?._id}/support-pool-settings`;
                return (
                    <a href={link} style={{color: "#1976D2", textDecoration: ""}}>
                        {poolInfo?.name || "-"}
                    </a>
                );
            }
        };

        const commonColumns = getCommonPoolColumnConfigs(onSwitch);

        if (type === "supporters") {
            const actionColumn = {
                title: "",
                key: "action",
                onCell: () => ({"data-label": "İşlemler"}),
                align: "center",
                render: (_, record) => (
                    <div style={{display: "flex", justifyContent: "center"}}>
                        <Space>
                            <Button type="primary" size="small" onClick={() => onEdit(record)}>
                                Düzenle
                            </Button>
                            <Popconfirm
                                title="Silmek istediğinize emin misiniz?"
                                okText="Evet"
                                cancelText="Hayır"
                                onConfirm={() => {
                                    onDelete(record);
                                }}
                            >
                                <Button danger size="small">
                                    Sil
                                </Button>
                            </Popconfirm>
                        </Space>
                    </div>
                )
            };
            return [poolNameColumn, ...commonColumns, actionColumn];
        }

        const badgeStatusColumn = {
            title: "Durum",
            key: "status",
            align: "center",
            onCell: () => ({"data-label": "Durum"}),
            render: (_, record) => (
                <div style={{display: "flex", justifyContent: "center"}}>
                    <span>
                        {record?.isActive ? (
                            <span
                                style={{
                                    background: "#52c41a",
                                    color: "white",
                                    padding: "2px 10px",
                                    borderRadius: 8
                                }}
                            >
                                Aktif
                            </span>
                        ) : (
                            <span
                                style={{
                                    background: "#f5222d",
                                    color: "white",
                                    padding: "2px 10px",
                                    borderRadius: 8
                                }}
                            >
                                Pasif
                            </span>
                        )}
                    </span>
                </div>
            )
        };
        const filteredCommonColumns = commonColumns.filter(col => col.key !== "status");
        return [poolNameColumn, ...filteredCommonColumns, badgeStatusColumn];
    }

    return (
        <PageContainer>
            <HeaderSection>
                <PageTitle>
                    Destek Havuz Ayarları
                    <span
                        style={{
                            color: COLORS.OWN_POOL,
                            fontSize: "1em",
                            fontWeight: "bold",
                            marginLeft: 8
                        }}
                    >
                        ({pool?.name || "-"})
                    </span>
                </PageTitle>
                <Button
                    onClick={() => {
                        window.open(`/panel/pools/${poolId}`, "_blank");
                    }}
                >
                    Havuz Detay <ArrowRightOutlined />
                </Button>
            </HeaderSection>
            <Legend
                supporters={poolSupporters}
                supportings={poolSupportings}
                hiddenSupporters={hiddenSupporters}
                hiddenSupportings={hiddenSupportings}
                onSupportersChange={setHiddenSupporters}
                onSupportingsChange={setHiddenSupportings}
                showSelf={showSelf}
                onShowSelfChange={setShowSelf}
                showUnknown={showUnknown}
                onShowUnknownChange={setShowUnknown}
                selectedCategory={selectedCategory}
                onCategorySelect={setSelectedCategory}
            />
            <MapContainer>
                <H3Map
                    ref={mapRef}
                    initialHexagons={filteredOwnHexagons}
                    showToolsPanel={false}
                    defaultZoom={10}
                    otherHexagons={filteredOtherHexagons}
                    isActionsEnabled={false}
                    style={{width: "100%", height: "100%"}}
                    showHexagonTooltip={true}
                />
            </MapContainer>
            <SectionHeader>
                <SectionColorIndicator color={COLORS.SUPPORTED_POOL} />
                <SectionTitle>Destek Alınan Havuzlar</SectionTitle>
            </SectionHeader>
            <ResponsiveDataTable
                columns={getColumns(handleEdit, handleDelete, handleSwitch, "supporters")}
                dataSource={poolSupporters}
                pagination={false}
                style={{marginBottom: 32}}
                bordered
                size="middle"
                loading={isPoolSupportersLoading}
            />
            <SectionHeader $marginTop={32}>
                <SectionColorIndicator color={COLORS.SUPPORTING_POOL} />
                <SectionTitle>Destek Verilen Havuzlar</SectionTitle>
            </SectionHeader>
            <ResponsiveDataTable
                columns={getColumns(handleEdit, handleDelete, handleSwitch, "supportings")}
                dataSource={poolSupportings}
                pagination={false}
                bordered
                size="middle"
                loading={isPoolSupportingsLoading}
            />
            <PoolSupporterEditModal
                visible={editModalVisible}
                onCancel={handleEditModalCancel}
                onSave={handleEditModalSave}
                initialValues={editInitialValues}
            />
            {definingPoolInfo && (
                <DefinePoolModal
                    visible={definePoolModalVisible}
                    onCancel={() => {
                        setDefinePoolModalVisible(false);
                    }}
                    onDefine={handleDefinePoolSubmit}
                    definingPoolInfo={definingPoolInfo}
                />
            )}
        </PageContainer>
    );
};

export default PoolSupporter;
